import { App, Button, Card, Col, Drawer, Form, Input, InputNumber, Modal, Row, Slider, Space, Spin, Switch, Tooltip } from "antd"
import { WarehouseContext } from "./"
import { useCallback, useContext, useEffect, useState } from "react"
import { EnvironmentOutlined, AimOutlined } from "@ant-design/icons"

import { Circle, MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet"
import L from "leaflet"
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
import { useMap } from "react-leaflet"

import GoogleCoverageMap from "../../components/maps/GoogleCoverageMap"

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 } // India
const DEFAULT_ZOOM = 13
const FOCUS_ZOOM = 16

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
})

const OSM_MARKER_ICON = new L.Icon({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})

// ── Module-level Leaflet sub-components — must NOT be inside AddWarehouse or React
// will create new component types on every render, causing Leaflet NaN errors.
const MapClickHandler = ({ onPick }) => {
    useMapEvents({ click: (e) => onPick(e.latlng) })
    return null
}

const MapInvalidateOnOpen = ({ trigger }) => {
    const map = useMap()
    useEffect(() => {
        const t = setTimeout(() => { try { map.invalidateSize() } catch { } }, 250)
        return () => clearTimeout(t)
    }, [map, trigger])
    return null
}

const MapFlyTo = ({ position, zoom }) => {
    const map = useMap()
    useEffect(() => {
        if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return
        const t = setTimeout(() => {
            try { map.flyTo([position.lat, position.lng], zoom ?? map.getZoom(), { animate: true, duration: 0.6 }) } catch { }
        }, 50)
        return () => clearTimeout(t)
    }, [map, position, zoom])
    return null
}

const AddWarehouse = () => {
    const { message } = App.useApp()

    const { form, onFinish, addModalOpen, onClose, handleSubmit, warehouseData, setIsEditMode, isEditMode } = useContext(WarehouseContext)

    const [loading, setLoading] = useState(false)
    const [mapPosition, setMapPosition] = useState(DEFAULT_CENTER)
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM)
    const [googleMapsApiKey, setGoogleMapsApiKey] = useState('')

    useEffect(() => {
        fetch(`${API_URL}/api/appConfig/public`)
            .then((r) => r.json())
            .then((res) => {
                const list = res?.data?.data || []
                const keyRow = list.find((r) => r.Key === 'Google Maps API Key')
                const enabledRow = list.find((r) => r.Key === 'Google Maps Enabled')
                const enabled = enabledRow?.value === 'true'
                if (enabled && keyRow?.value) setGoogleMapsApiKey(keyRow.value)
                else setGoogleMapsApiKey('')
            })
            .catch(() => {})
    }, [])

    const parseNumber = (value) => {
        const n = Number(value)
        return Number.isFinite(n) ? n : null
    }

    const reverseGeocodeAndFill = useCallback(async (latitude, longitude) => {
        try {
            if (googleMapsApiKey) {
                // Google Maps Geocoding API
                const res = await fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${googleMapsApiKey}`
                )
                const data = await res.json()
                if (data.status !== 'OK' || !data.results?.length) {
                    message.error("Google Maps: Could not fetch address.")
                    return
                }
                const components = data.results[0].address_components
                const get = (type) => components.find((c) => c.types.includes(type))?.long_name || ''
                const getShort = (type) => components.find((c) => c.types.includes(type))?.short_name || ''
                const locality = get('locality') || get('sublocality') || get('neighborhood')
                const city = get('locality') || get('administrative_area_level_2') || locality
                form.setFieldsValue({
                    latitude,
                    longitude,
                    address: data.results[0].formatted_address || form.getFieldValue("address"),
                    city: city || form.getFieldValue("city"),
                    state: get('administrative_area_level_1') || form.getFieldValue("state"),
                    country: get('country') || form.getFieldValue("country"),
                    pincode: get('postal_code') || form.getFieldValue("pincode"),
                })
            } else {
                // Fallback: BigDataCloud (free, no key)
                const res = await fetch(
                    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                )
                const data = await res.json()
                if (!data) return
                form.setFieldsValue({
                    latitude,
                    longitude,
                    address: `${data.locality || ''}, ${data.city || ''}`.replace(/^,\s*|,\s*$/, '').trim() || form.getFieldValue("address"),
                    city: data.city || data.locality || form.getFieldValue("city"),
                    state: data.principalSubdivision || form.getFieldValue("state"),
                    country: data.countryName || form.getFieldValue("country"),
                    pincode: data.postcode || form.getFieldValue("pincode"),
                })
            }
        } catch {
            message.error("Error fetching address data.")
        }
    }, [form, googleMapsApiKey])

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            message.error("Geolocation is not supported in this browser.")
            return
        }

        setLoading(true)
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords
                    setMapPosition({ lat: latitude, lng: longitude })
                    setMapZoom(FOCUS_ZOOM)
                    await reverseGeocodeAndFill(latitude, longitude)
                    message.success("Address auto-filled successfully!")
                } catch (e) {
                    message.error("Failed to fetch address for current location.")
                } finally {
                    setLoading(false)
                }
            },
            (error) => {
                setLoading(false)
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        Modal.warning({
                            title: "Location Permission Blocked",
                            centered: true,
                            content: (
                                <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                                    <p>Location access has been <b>blocked</b> in your browser settings, preventing automatic location detection.</p>
                                    <p style={{ fontWeight: 600, marginBottom: 6 }}>How to allow location access:</p>
                                    <ol style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Click the <b>🔒 (Lock) or ⚙️ (Tune / Site Settings)</b> icon on the left side of your browser&apos;s address bar.</li>
                                        <li>Find <b>Location</b> and change the permission to <b>Allow</b>.</li>
                                        <li>On macOS, ensure <b>System Settings &gt; Privacy &amp; Security &gt; Location Services</b> is turned ON for your browser.</li>
                                        <li><b>Reload / Refresh</b> the page and click <b>&quot;Use Current Location&quot;</b> again.</li>
                                    </ol>
                                </div>
                            ),
                            okText: "Got it",
                        })
                        break
                    case error.POSITION_UNAVAILABLE:
                        message.error("Location information is unavailable. Please check your GPS / network connection.")
                        break
                    case error.TIMEOUT:
                        message.error("Location request timed out. Please try again.")
                        break
                    default:
                        message.error(`Location error: ${error.message || "Unable to retrieve location."}`)
                        break
                }
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        )
    }

    const radiusKmWatch = Form.useWatch("deliveryRadiusKm", form)
    const radiusKm = (radiusKmWatch != null && Number(radiusKmWatch) > 0) ? Number(radiusKmWatch) : 10
    const radiusEnabled = Form.useWatch("radiusEnabled", form) ?? false

    useEffect(() => {
        if (!addModalOpen) return
        const lat = parseNumber(form.getFieldValue("latitude"))
        const lng = parseNumber(form.getFieldValue("longitude"))
        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
    }, [addModalOpen, form])

    return (
        <Drawer
            title={isEditMode ? "Edit Warehouse" : "Create a new Warehouses"}
            width={720}
            onClose={onClose}
            open={addModalOpen}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleSubmit}>
                        {isEditMode ? "Update" : "Submit"}
                    </Button>
                </Space>
            }
        >
            <Card>
                <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Warehouse Name" name="name" rules={[{ required: true, message: "Please enter warehouse name" }]}>
                                <Input placeholder="Enter warehouse name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Address" name="address" rules={[{ required: true, message: "Please enter address" }]}>
                                <Input placeholder="Enter street address" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="City" name="city" rules={[{ required: true, message: "Please enter city" }]}>
                                <Input placeholder="Enter city name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="State" name="state" rules={[{ required: true, message: "Please enter state" }]}>
                                <Input placeholder="Enter state name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Country" name="country" rules={[{ required: true, message: "Please enter country" }]}>
                                <Input placeholder="Enter country name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Pincode"
                                name="pincode"
                                rules={[
                                    { required: true, message: "Please enter pincode" },
                                    { pattern: /^[0-9]{5,6}$/, message: "Invalid pincode format" }
                                ]}
                            >
                                <Input placeholder="Enter pincode" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Manager" name="manager" rules={[{ required: true, message: "Please enter manager name" }]}>
                                <Input placeholder="Enter manager name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Contact Phone"
                                name="contactPhone"
                                rules={[
                                    { required: true, message: "Please enter contact phone" },
                                    { pattern: /^\+?[0-9]{10,15}$/, message: "Invalid phone number format" }
                                ]}
                            >
                                <Input placeholder="Enter contact phone" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Latitude" name="latitude" rules={[{ required: true, message: "Please enter latitude" }]}>
                                <Input
                                    placeholder="Enter latitude (e.g., 21.5724)"
                                    onBlur={() => {
                                        const lat = parseNumber(form.getFieldValue("latitude"))
                                        const lng = parseNumber(form.getFieldValue("longitude"))
                                        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Longitude" name="longitude" rules={[{ required: true, message: "Please enter longitude" }]}>
                                <Input
                                    placeholder="Enter longitude (e.g., 72.90)"
                                    onBlur={() => {
                                        const lat = parseNumber(form.getFieldValue("latitude"))
                                        const lng = parseNumber(form.getFieldValue("longitude"))
                                        if (lat !== null && lng !== null) setMapPosition({ lat, lng })
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 8, marginBottom: 16 }}>
                        <Col span={24}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 16px', background: '#f8fafc',
                                border: '1px solid #e2e8f0', borderRadius: 10,
                            }}>
                                <Form.Item name="radiusEnabled" valuePropName="checked" noStyle>
                                    <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                                </Form.Item>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>Enable Delivery Coverage Radius</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                        {radiusEnabled
                                            ? <>When ON, only orders within <strong>{radiusKm} km</strong> of this warehouse will be eligible for dispatch.</>
                                            : 'Turn ON to restrict orders by delivery distance from this warehouse.'}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {radiusEnabled && (
                        <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                            <Col span={24}>
                                <Form.Item
                                    label="Delivery Coverage Radius (km)"
                                    name="deliveryRadiusKm"
                                    tooltip="Orders within this distance from the warehouse will be eligible for delivery."
                                    rules={[{ required: true, message: "Please specify delivery radius" }]}
                                >
                                    <Row gutter={16} align="middle">
                                        <Col span={16}>
                                            <Slider min={1} max={100} step={0.5} value={radiusKm}
                                                onChange={(val) => form.setFieldsValue({ deliveryRadiusKm: val })} />
                                        </Col>
                                        <Col span={8}>
                                            <InputNumber min={0.5} max={500} step={0.5} style={{ width: "100%" }}
                                                addonAfter="km" value={radiusKm}
                                                onChange={(val) => form.setFieldsValue({ deliveryRadiusKm: Number(val) || 1 })} />
                                        </Col>
                                    </Row>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Button type="primary" onClick={getCurrentLocation} block disabled={loading} icon={<EnvironmentOutlined />}>
                                {loading ? <Spin /> : "Use current location"}
                            </Button>
                        </Col>
                    </Row>

                    <div style={{ marginTop: 16 }}>
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <AimOutlined style={{ color: "#1677ff" }} />
                                    <span>Warehouse Location &amp; Delivery Zone Preview</span>
                                </Space>
                            }
                            extra={<span style={{ fontSize: "12px", color: radiusEnabled ? "#1677ff" : "#aaa" }}>{radiusEnabled ? `Blue circle = ${radiusKm} km delivery radius` : 'Radius disabled'}</span>}
                        >
                            <div style={{ height: 350, width: "100%", borderRadius: "8px", overflow: "hidden" }}>
                                {Number.isFinite(mapPosition?.lat) && Number.isFinite(mapPosition?.lng) && (
                                    googleMapsApiKey ? (
                                        <GoogleCoverageMap
                                            apiKey={googleMapsApiKey}
                                            position={mapPosition}
                                            zoom={mapZoom}
                                            radiusEnabled={radiusEnabled}
                                            radiusKm={radiusKm}
                                            onPick={async (latlng) => {
                                                setMapPosition({ lat: latlng.lat, lng: latlng.lng })
                                                setMapZoom(FOCUS_ZOOM)
                                                await reverseGeocodeAndFill(latlng.lat, latlng.lng)
                                            }}
                                        />
                                    ) : (
                                        <MapContainer center={[mapPosition.lat, mapPosition.lng]} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
                                            <MapInvalidateOnOpen trigger={addModalOpen} />
                                            <MapFlyTo position={mapPosition} zoom={mapZoom} />
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <MapClickHandler
                                                onPick={async (latlng) => {
                                                    setMapPosition({ lat: latlng.lat, lng: latlng.lng })
                                                    setMapZoom(FOCUS_ZOOM)
                                                    await reverseGeocodeAndFill(latlng.lat, latlng.lng)
                                                }}
                                            />
                                            <Marker
                                                position={[mapPosition.lat, mapPosition.lng]}
                                                icon={OSM_MARKER_ICON}
                                                draggable
                                                eventHandlers={{
                                                    dragend: async (e) => {
                                                        const latlng = e.target.getLatLng()
                                                        setMapPosition({ lat: latlng.lat, lng: latlng.lng })
                                                        setMapZoom(FOCUS_ZOOM)
                                                        await reverseGeocodeAndFill(latlng.lat, latlng.lng)
                                                    },
                                                }}
                                            />
                                            {radiusEnabled && (
                                                <Circle
                                                    center={[mapPosition.lat, mapPosition.lng]}
                                                    radius={(Number(radiusKm) || 10) * 1000}
                                                    pathOptions={{ color: "#1677ff", fillColor: "#1677ff", fillOpacity: 0.18, weight: 2, dashArray: "4, 6" }}
                                                />
                                            )}
                                        </MapContainer>
                                    )
                                )}
                            </div>
                        </Card>
                    </div>
                </Form>
            </Card>
        </Drawer>
    )
}
export default AddWarehouse
